import InfiniteScroll from "react-infinite-scroll-component";
import { AccessLevel } from "@/api/williams/users";
import { ContractorFilters, ContractorListView } from "@/api/williams/contractors";
import { DateTime, highlight } from "@/util";
import { ExpandableCard, PinButton } from "../../components";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { faSquarePlus } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFullPath, routes } from "../../routes";
import { Link } from "react-router-dom";
import { useContractorList, usePinContractor } from "../hooks";
import { usePermissions } from "../../users/hooks";
import { useState } from "react";

export type ContractorProps = {
  filters: Partial<ContractorFilters>;
  expanded?: boolean;
  allowExpand?: boolean;
};

export function Contractors({ filters, ...props }: ContractorProps) {
  const contractors = useContractorList(filters);
  const permissions = usePermissions();

  const data = contractors.data?.pages.flatMap((page) => page.results) ?? [];
  const total = contractors.data?.pages[0]?.total ?? 0;
  const readOnly = permissions.data?.accessLevel === AccessLevel.GeneralUser;

  return (
    <ExpandableCard
      caption={`Contractors (${total})`}
      expanded={props.expanded}
      allowExpand={props.allowExpand}
      headerButtons={[
        <Link to={getFullPath(routes.contractor.create)} hidden={readOnly}>
          <FontAwesomeIcon icon={faSquarePlus} size="sm" title="Add Contractor" />
        </Link>,
      ]}
    >
      <InfiniteScroll
        dataLength={data.length}
        next={contractors.fetchNextPage}
        hasMore={Boolean(contractors.hasNextPage)}
        loader={<p>Loading...</p>}
      >
        {contractors.isLoading ? (
          <p>Loading...</p>
        ) : contractors.isError ? (
          <p>An unknown error occurred.</p>
        ) : contractors.isSuccess && data.length > 0 ? (
          <ContractorTable data={data} search={filters.searchString} />
        ) : filters.searchString ? (
          <p>Your search returned no results.</p>
        ) : (
          <p>No contractors found.</p>
        )}
      </InfiniteScroll>
    </ExpandableCard>
  );
}

function ContractorTable({ data, search }: { data: ContractorListView[]; search?: string }) {
  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          <th className="col">Name</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((contractor) => (
          <ContractorRow key={contractor.id} contractor={contractor} search={search} />
        ))}
      </tbody>
    </table>
  );
}

function ContractorRow({ contractor, search }: { contractor: ContractorListView; search?: string }) {
  const [expanded, setExpanded] = useState<boolean>(false);

  const pin = usePinContractor();

  return (
    <>
      <tr key={`${contractor.id}_${contractor.pinned}`}>
        <td>
          <Link to={getFullPath(routes.contractor.open(contractor.id))} className="link link-primary">
            {highlight(contractor.name, search)}
          </Link>
        </td>
        <td className="text-end text-nowrap">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
          <PinButton pinned={contractor.pinned} onChange={() => pin.mutate(contractor.id)} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={2}>
            <ExpandedDetails contractor={contractor} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ contractor }: { contractor: ContractorListView }) {
  return (
    <div className="ms-1">
      <div className="bg-light p-1">Contractor Details</div>
      <div className="p-1">
        <dl>
          {contractor.shopOrField && (
            <>
              <dt>Shop or Field</dt>
              <dd>{contractor.shopOrField}</dd>
            </>
          )}
          <>
            <dt>Last used date</dt>
            <dd>{DateTime.getLocalDate(contractor.lastUsedDate) ?? "Never"}</dd>
          </>
          {contractor.weldingSpecialist && (
            <>
              <dt>Welding Specialist</dt>
              <dd>{contractor.weldingSpecialist}</dd>
            </>
          )}
          {contractor.regions && (
            <>
              <dt>Region(s)</dt>
              <dd>{contractor.regions}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}
